import React, { useEffect, useMemo } from "react"
import { hasPermission } from "../../../helpers/helpers"
import { Card, Button, CardBody, Row, Col } from "reactstrap"
import paginationFactory, {
  PaginationListStandalone,
  PaginationProvider,
} from "react-bootstrap-table2-paginator"
import ToolkitProvider, {
  CSVExport,
  Search,
} from "react-bootstrap-table2-toolkit"
import Select from "react-select"
import BootstrapTable from "react-bootstrap-table-next"
import * as moment from "moment"
import {
  ApproveWithdrawalButton,
  DeclineWithdrawalButton,
  VerifyWithdrawalButton,
} from "./action"

function WithdrawalsTable(props) {
  const { data, autoReleasedAmount } = props
  const Columns = useMemo(
    () => [
      {
        dataField: "msisdn",
        text: "Mobile",
        sort: true,
        formatter: (cell, row) => (
          <>
            {row?.is_from_auto_released == 1 ? row?.mobile_number : row?.msisdn}
          </>
        ),
      },
      {
        dataField: "channel",
        text: "Channel",
        sort: true,
      },
      {
        dataField: "payment_type",
        text: "Type",
        sort: true,
      },
      {
        dataField: "amount",
        text: "Amount",
        sort: true,
        formatter: (cell, row) => (
          <> {Number(Number(row?.amount).toFixed(2)).toLocaleString("en-GB")}</>
        ),
      },
      {
        dataField: "success_at",
        text: "Date",
        sort: true,
        formatter: (cell, row) => {
          const dateString = row?.success_at

          if (!dateString) return "N/A"

          const date = new Date(dateString)
          if (isNaN(date)) return "Invalid Date"

          const formattedDate = date.toLocaleString("en-GB", {
            timeZone: "Africa/Harare",
            hour12: true,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })

          // Capitalize AM/PM
          const capitalizedDate = formattedDate.replace(/(am|pm)/gi, match =>
            match.toUpperCase()
          )

          return <>{capitalizedDate}</>
        },
      },
      {
        dataField: "reference_id",
        text: "Reference",
        sort: true,
      },
      // {
      //   dataField: "operatorreference",
      //   text: "Operator Reference",
      //   sort: true,
      // },
      // {
      //   dataField: "customerreference",
      //   text: "Customer Reference",
      //   sort: true,
      // },
      {
        dataField: "txn_status",
        text: "Status",
        sort: true,
        formatter: (cell, row) => {
          const status = row?.txn_status?.toLowerCase()
          if (status === "manual_review") {
            return (
              <span className="badge badge-soft-warning font-size-12 px-2 py-1">
                <i className="mdi mdi-flag me-1" />
                Manual Review
              </span>
            )
          }
          if (status === "pending") {
            return (
              <span className="badge badge-soft-primary font-size-12">
                Pending
              </span>
            )
          }
          if (status === "success") {
            return (
              <span className="badge badge-soft-success font-size-12">
                Success
              </span>
            )
          }
          return (
            <span className="badge badge-soft-secondary font-size-12">
              {row?.txn_status || "—"}
            </span>
          )
        },
      },
      {
        dataField: "flag_reason",
        text: "Flag Reason",
        sort: true,
        formatter: (cell, row) => {
          if (row?.txn_status?.toLowerCase() === "manual_review") {
            return (
              <span className="text-warning font-size-12">
                <i className="mdi mdi-information-outline me-1" />
                {row?.flag_reason || "Deposit without play"}
              </span>
            )
          }
          return <span className="text-muted">—</span>
        },
      },
      {
        dataField: "action",
        text: "Action",
        sort: true,
        formatter: (cell, row) => {
          const status = row?.txn_status?.toLowerCase()
          if (status === "manual_review") {
            return (
              <div className="d-flex gap-2">
                <ApproveWithdrawalButton
                  rowData={row}
                  call={props.call}
                  token={props.token}
                />
                <DeclineWithdrawalButton
                  rowData={row}
                  call={props.call}
                  token={props.token}
                />
              </div>
            )
          }
          if (Number(row?.amount) > Number(autoReleasedAmount)) {
            return status === "pending" ? (
              <div className="d-flex gap-2">
                <ApproveWithdrawalButton
                  rowData={row}
                  call={props.call}
                  token={props.token}
                />
                <DeclineWithdrawalButton
                  rowData={row}
                  call={props.call}
                  token={props.token}
                />
              </div>
            ) : (
              <VerifyWithdrawalButton
                rowData={row}
                call={props.call}
                token={props.token}
              />
            )
          }
          return <span className="text-muted">—</span>
        },
      },
    ],
    [autoReleasedAmount]
  )

  const ManualColumn = [
    {
      dataField: "transaction_to.phone_number",
      text: "Transaction To",
      sort: true,
      formatter: (cell, row) => <> {row?.transaction_to?.phone_number}</>,
    },
    {
      dataField: "transaction_by.name",
      text: "Transaction By",
      sort: true,
      formatter: (cell, row) => <> {row?.transaction_by?.name}</>,
    },
    {
      dataField: "type",
      text: "Type",
      sort: true,
    },
    {
      dataField: "amount",
      text: "Amount",
      sort: true,
      formatter: (cell, row) => <>{row?.amount}</>,
    },
    {
      dataField: "created_at",
      text: "Date",
      sort: true,
      formatter: (cell, row) => {
        const date = new Date(row?.created_at)
        const formattedDate = date.toLocaleString("en-GB", {
          timeZone: "CAT",
          hour12: true,
        })
        const capitalizedDate = formattedDate.replace(/(am|pm)/gi, match =>
          match.toUpperCase()
        )
        return <>{capitalizedDate}</>
      },
    },
    {
      dataField: "reason",
      text: "Reason",
      sort: true,
    },
  ]

  const ManualReviewColumn = [
    {
      dataField: "msisdn",
      text: "Mobile",
      sort: true,
      formatter: (cell, row) => (
        <>
          {row?.is_from_auto_released == 1 ? row?.mobile_number : row?.msisdn}
        </>
      ),
    },
    {
      dataField: "channel",
      text: "Channel",
      sort: true,
    },
    {
      dataField: "amount",
      text: "Amount",
      sort: true,
      formatter: (cell, row) => (
        <>{Number(Number(row?.amount).toFixed(2)).toLocaleString("en-GB")}</>
      ),
    },
    {
      dataField: "txn_status",
      text: "Flag Status",
      sort: true,
      formatter: () => (
        <span className="badge badge-soft-warning font-size-12 px-2 py-1">
          <i className="mdi mdi-flag me-1" />
          Manual Review
        </span>
      ),
    },
    {
      dataField: "flag_reason",
      text: "Flag Reason",
      sort: true,
      formatter: (cell, row) => (
        <span className="text-warning font-size-12">
          <i className="mdi mdi-information-outline me-1" />
          {row?.flag_reason || "Deposit without play"}
        </span>
      ),
    },
    {
      dataField: "success_at",
      text: "Date",
      sort: true,
      formatter: (cell, row) => {
        const dateString = row?.success_at
        if (!dateString) return "N/A"
        const date = new Date(dateString)
        if (isNaN(date)) return "Invalid Date"
        const formattedDate = date.toLocaleString("en-GB", {
          timeZone: "Africa/Harare",
          hour12: true,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })
        return <>{formattedDate.replace(/(am|pm)/gi, m => m.toUpperCase())}</>
      },
    },
    {
      dataField: "reference_id",
      text: "Reference",
      sort: true,
    },
    {
      dataField: "action",
      text: "Action",
      sort: true,
      formatter: (_cell, row) => (
        <div className="d-flex gap-2">
          <ApproveWithdrawalButton
            rowData={row}
            call={props.call}
            token={props.token}
          />
          <DeclineWithdrawalButton
            rowData={row}
            call={props.call}
            token={props.token}
          />
        </div>
      ),
    },
  ]

  useEffect(() => {
    props.call()
  }, [props.withdrawType])

  const defaultSorted = [
    {
      dataField: "msisdn",
      order: "desc",
    },
  ]

  const optionsWithdraw = [
    { label: "TotalPayment", value: "TotalPayment" },
    { label: "ManualPayment", value: "ManualPayment" },
    { label: "ManualReview", value: "ManualReview" },
  ]
  const { SearchBar } = Search
  //pagination customization
  const { ExportCSVButton } = CSVExport

  const pageOptions = {
    sizePerPage: 10,
    totalSize: data.length,
    custom: true,
  }
  return (
    <Card>
      <CardBody>
        <div className="mb-4 h4 card-title">{"Withdrawals"}</div>
        {props.withdrawType === "ManualReview" && (
          <div
            className="alert d-flex align-items-center mb-3 py-2 px-3"
            style={{
              background: "#fff8e1",
              border: "1px solid #ffc107",
              borderRadius: 6,
            }}
            role="alert"
          >
            <i className="mdi mdi-flag text-warning me-2 font-size-18" />
            <span className="text-dark">
              <strong className="text-warning">{data.length}</strong> withdrawal
              {data.length !== 1 ? "s" : ""} flagged for{" "}
              <strong>Manual Review</strong>
            </span>
          </div>
        )}
        <PaginationProvider
          key={props.withdrawType}
          pagination={paginationFactory(pageOptions)}
          keyField="id"
          columns={
            props.withdrawType == "ManualPayment"
              ? ManualColumn
              : props.withdrawType == "ManualReview"
              ? ManualReviewColumn
              : Columns
          }
          data={data}
        >
          {({ paginationProps, paginationTableProps }) => (
            <ToolkitProvider
              keyField="id"
              data={data}
              columns={
                props.withdrawType == "ManualPayment"
                  ? ManualColumn
                  : props.withdrawType == "ManualReview"
                  ? ManualReviewColumn
                  : Columns
              }
              bootstrap4
              search
              exportCSV={{
                onlyExportFiltered: true,
                exportAll: false,
                fileName: `Withdraw-record-${moment(new Date()).format(
                  "DD/MM/YYYY"
                )}.csv`,
              }}
            >
              {toolkitProps => (
                <React.Fragment>
                  <Row className="mb-2">
                    <Col sm="6">
                      <Row>
                        <Col sm="4">
                          <div className="search-box me-2 mb-2 d-inline-block">
                            <div className="position-relative">
                              <SearchBar {...toolkitProps.searchProps} />
                              <i className="bx bx-search-alt search-icon" />
                            </div>
                          </div>
                        </Col>
                        <Col sm="4">
                          <Select
                            options={optionsWithdraw}
                            maxMenuHeight={150}
                            classNamePrefix="select2-selection"
                            onChange={item => {
                              props.setWithdrawType(item.label)
                            }}
                            value={{
                              label: props.withdrawType,
                              value: props.withdrawType,
                            }}
                          />
                          {/*<Select*/}
                          {/*  options={[*/}
                          {/*    { label: "Pending", value: "Pending" },*/}
                          {/*    { label: "Success", value: "Success" },*/}

                          {/*    { label: "Failed", value: "Failed" },*/}
                          {/*    { label: "Rejected", value: "Rejected" },*/}
                          {/*  ]}*/}
                          {/*  maxMenuHeight={150}*/}
                          {/*  classNamePrefix="select2-selection"*/}
                          {/*/>*/}
                        </Col>
                        <Col sm="4">
                          <div>
                            <ExportCSVButton
                              {...toolkitProps.csvProps}
                              className="download-table-xls-button w-md btn btn-primary"
                            >
                              Download
                            </ExportCSVButton>
                          </div>
                        </Col>
                      </Row>
                    </Col>
                    {/*<Col sm={6}>*/}
                    {/*  {hasPermission("create-withdrawals") && (*/}
                    {/*    <Col sm="12">*/}
                    {/*      <div className="text-sm-end">*/}
                    {/*        <Button*/}
                    {/*          type="button"*/}
                    {/*          color="success"*/}
                    {/*          className="btn-rounded  mb-2 me-2"*/}
                    {/*        >*/}
                    {/*          <i className="mdi mdi-plus me-1" />*/}
                    {/*          Add New*/}
                    {/*        </Button>*/}
                    {/*      </div>*/}
                    {/*    </Col>*/}
                    {/*  )}*/}
                    {/*</Col>{" "}*/}
                  </Row>
                  <Row>
                    <Col xl="12">
                      <div className="table-responsive">
                        <BootstrapTable
                          keyField="id"
                          responsive
                          bordered={false}
                          striped={false}
                          defaultSorted={defaultSorted}
                          //   selectRow={selectRow}
                          classes={
                            "table align-middle table-nowrap table-check"
                          }
                          headerWrapperClasses={"table-light"}
                          {...toolkitProps.baseProps}
                          {...paginationTableProps}
                          rowClasses={row =>
                            `mytable-hover-row${
                              props.withdrawType !== "ManualReview" &&
                              row?.txn_status?.toLowerCase() === "manual_review"
                                ? " table-warning"
                                : ""
                            }`
                          }
                        />
                      </div>
                    </Col>
                  </Row>
                  <div className="pagination pagination-rounded justify-content-end">
                    <PaginationListStandalone {...paginationProps} />
                  </div>
                </React.Fragment>
              )}
            </ToolkitProvider>
          )}
        </PaginationProvider>
      </CardBody>
    </Card>
  )
}

export default WithdrawalsTable
